import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { Download, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { BrandNav, SiteFooter, ContactCTA } from '../components/SiteChrome';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENGINES = ['energy', 'pressure', 'position', 'momentum', 'environment'];

const ReadCard = ({ card, innerRef }) => (
  <div ref={innerRef} className="bg-black border-2 border-brand rounded-2xl p-6 sm:p-8 w-full max-w-[560px]" data-testid="read-card">
    <div className="flex items-center gap-2.5 mb-6">
      <img src="/pmi-icon-192.png" alt="" className="w-8 h-8 rounded-md" />
      <span className="font-bold">Trade PMI</span>
      <span className="text-zinc-500 text-xs">by Standards Protocol</span>
    </div>
    <p className="font-mono text-3xl font-bold" data-testid="card-ticker">{card.ticker}</p>
    <p className="text-xs text-zinc-500 mb-5">{card.asset_name} · {card.market}</p>
    <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Trade state</p>
    <p className="text-2xl font-bold text-brand mb-1" data-testid="card-state">{card.trade_state}</p>
    <p className="text-xs text-zinc-500 mb-4">Confidence: {card.confidence || 'n/a'}</p>
    <p className="text-sm text-zinc-300 mb-5 leading-relaxed" data-testid="card-verdict">{card.verdict}</p>
    <div className="grid grid-cols-1 gap-1.5 mb-5">
      {ENGINES.filter((k) => card.engines?.[k]).map((k) => (
        <div key={k} className="flex justify-between text-xs border-b border-zinc-800/60 pb-1.5">
          <span className="text-zinc-500 capitalize">{k}</span>
          <span className="font-mono text-zinc-200">{card.engines[k]}</span>
        </div>
      ))}
    </div>
    <p className="text-[10px] text-zinc-600">
      Read at {new Date(card.analysis_timestamp).toLocaleString()} · Descriptive data only. Not financial advice.
    </p>
  </div>
);

export default function SharedCardPage() {
  const { cardId } = useParams();
  const ref = useRef(null);
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/share-cards/${encodeURIComponent(cardId)}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Card not found');
        setCard(d);
        sessionStorage.setItem('tpmi_via_card', d.ticker || '');
        let device = localStorage.getItem('tpmi_device');
        if (!device) { device = crypto.randomUUID(); localStorage.setItem('tpmi_device', device); }
        fetch(`${API_URL}/api/share-cards/${encodeURIComponent(cardId)}/open`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: device }),
        }).catch(() => {});
      })
      .catch((e) => setError(e.message));
  }, [cardId]);

  const download = async () => {
    setSaving(true);
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, backgroundColor: '#000000', cacheBust: true });
      const a = document.createElement('a');
      a.href = url; a.download = `trade-pmi-${card.ticker}.png`; a.click();
    } catch { toast.error('Image download failed. Please try again.'); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <BrandNav width="max-w-4xl" />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-10 w-full flex flex-col items-center gap-6">
        {error && <p className="text-sm text-red-400" data-testid="card-error">{error}</p>}
        {!card && !error && <Loader2 className="w-6 h-6 animate-spin text-brand" data-testid="card-loading" />}
        {card && (
          <>
            <ReadCard card={card} innerRef={ref} />
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to={`/?ticker=${encodeURIComponent(card.ticker)}&market=${card.market}`} data-testid="card-fresh-analysis-link"
                className="bg-brand hover:bg-brand-light text-black text-sm font-bold px-5 py-2.5 rounded-md inline-flex items-center gap-1.5 transition-colors">
                Run a fresh analysis <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={download} disabled={saving} data-testid="card-download-btn"
                className="border border-zinc-700 hover:border-brand/60 text-zinc-300 text-sm px-5 py-2.5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download image
              </button>
            </div>
            <p className="text-[11px] text-zinc-600 text-center max-w-md">This is a snapshot from the time shown. Market conditions change, so run a fresh analysis for the current read.</p>
          </>
        )}
        <ContactCTA className="w-full mt-4" source="shared_card" ticker={card?.ticker} />
      </main>
      <SiteFooter />
    </div>
  );
}
